import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Controls the conflict modal in Plantafel using native <dialog> element.
// The dialog is shown via showModal() which places it in the browser's top layer,
// above all other content regardless of z-index.
export default class extends Controller {
  static values = {
    taskId: Number,
    resourceId: Number,
    resourceType: String,
  }

  connect() {
    // Show dialog as modal - this places it in the browser's top layer
    this.element.showModal()

    // Handle native close event (triggered by Escape key or close() call)
    this.boundHandleClose = this.handleClose.bind(this)
    this.element.addEventListener("close", this.boundHandleClose)

    // Close when clicking the backdrop (the dialog element itself, not its children)
    this.boundHandleClick = this.handleClick.bind(this)
    this.element.addEventListener("click", this.boundHandleClick)
  }

  disconnect() {
    this.element.removeEventListener("close", this.boundHandleClose)
    this.element.removeEventListener("click", this.boundHandleClick)
  }

  handleClose() {
    // Remove from DOM after dialog closes
    this.element.remove()
  }

  handleClick(event) {
    // Close when clicking the backdrop (the dialog element itself)
    // This works because the dialog's ::backdrop is part of the dialog element
    if (event.target === this.element) {
      this.close()
    }
  }

  close() {
    // Use native dialog close - this triggers the 'close' event
    this.element.close()
  }

  proceed() {
    // Get the CSRF token
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    // Get the view mode, vertical group by, and start date from the main plantafel controller
    const plantafelContainer = document.querySelector('[data-controller="plantafel"]')
    const viewMode = plantafelContainer?.dataset.plantafelViewModeValue || "week"
    const verticalGroupBy = plantafelContainer?.dataset.plantafelVerticalGroupByValue || "trade"
    const viewStartDate = plantafelContainer?.dataset.plantafelCurrentDateValue || ""

    // Make the assignment request with force=true
    const url = `/plantafel/tasks/${this.taskIdValue}/assign_resource`
    const data = {
      resource_id: this.resourceIdValue,
      resource_type: this.resourceTypeValue,
      force: true,
      view_mode: viewMode,
      vertical_group_by: verticalGroupBy,
      view_start_date: viewStartDate,
    }

    // Close modal first to avoid UI issues
    this.close()

    fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        if (response.ok && contentType.includes("text/vnd.turbo-stream.html")) {
          // Let Turbo handle the replace_all stream to update badges
          Turbo.renderStreamMessage(html)

          // Apply conflict styling to task bars (since this was a force assignment with conflicts)
          this.applyConflictStylingToTask(this.taskIdValue)
        } else {
          Honeybadger.notify(`Failed to force assign resource: ${response.status}`, {
            context: {
              level: "error",
              component: "plantafel-conflict-modal",
              action: "force_assign",
            },
          })
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-conflict-modal", action: "force_assign" },
        })
      })
  }

  // Apply conflict styling to all task bars with the given task ID
  applyConflictStylingToTask(taskId) {
    // Find all task bar containers for this task
    const taskContainers = document.querySelectorAll(`[data-task-id="${taskId}"].relative.h-10`)

    taskContainers.forEach((container) => {
      // Find the task bar element inside
      const taskBar = container.querySelector('[data-controller*="plantafel-element"]')

      if (taskBar) {
        // Apply conflict styling
        taskBar.style.background =
          "repeating-linear-gradient(-45deg, transparent, transparent 8px, rgba(232, 79, 79, 0.15) 8px, rgba(232, 79, 79, 0.15) 9px), #D9EFE9"
        taskBar.style.border = "1px solid #E84F4F"

        // Update the color indicator dot
        const dot = taskBar.querySelector(".rounded-full.mr-1\\.5")
        if (dot) {
          dot.style.backgroundColor = "#E84F4F"
        }
      }

      // Add tooltip controller if not already present
      if (!container.hasAttribute("data-controller")) {
        container.setAttribute("data-controller", "plantafel-tooltip")
      }
    })
  }
}
